/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import {
    ConfigurationParameters,
    Configuration,
    RatingsApi,
    InternalRatingsRequest,
} from 'client-code/internal-ratings';
import { IInternalRatingsServiceRepository } from '../interfaces/IInternalRatingsServiceRepository';
import { Log } from '../logging/Log';
import { TServicesParams } from '../types/TServicesParams';
import { Constants } from '../constants';
import { CommonHeaders } from '../CommonHeaders';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';
import { Storage } from '../storage/Storage';
import { CachingPolicy } from '../cachingpolicy/CachingPolicy';
import { DeviceModel } from '../device/DeviceModel';
import { APPLICATION_FEATURE_AREA } from '../../config';

export class InternalRatingsServiceRepositoryImpl implements IInternalRatingsServiceRepository {
    mParams: TServicesParams;

    TAG: string = 'InternalRatingsServiceRepositoryImpl';

    private static INTERNAL_RATINGS_MAP: Map<string, string> = new Map();

    constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private async getInternalRatingsFromService(): Promise<void> {
        try {
            const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.STARTUP).headers;

            const configurationParameters: ConfigurationParameters = {
                apiKey: this.mParams.accessToken,
                basePath: this.mParams.basePath,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: commonHeaders,
                },
            };

            const requestParameters: InternalRatingsRequest = {
                applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
                applicationName: DeviceModel.getTiVoSettings().getAppName(),
                applicationVersion: DeviceModel.getAppVersionName(),
                bodyId: this.mParams.bodyId,
                productName: DeviceModel.getTiVoSettings().getAppName(),
                msoPartnerId: DeviceModel.getTiVoSettings().getMsoPartnerId(),
                deviceType: DeviceModel.getTiVoSettings().getDeviceType(),
                xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
            };

            const configuration = new Configuration(configurationParameters);
            const internalRatingsService = new RatingsApi(configuration);
            const internalRatingsServiceResponse = await internalRatingsService.internalRatings(
                requestParameters,
            );

            Storage.getInstance().setItem(
                Storage.Keys.INTERNAL_RATINGS_SERVICE_DATA,
                internalRatingsServiceResponse.data,
            );
        } catch (error) {
            Log.e(this.TAG, 'fetchInternalRatings', error);
            throw error;
        }
    }

    async fetchAndStoreInternalRatingsData(): Promise<void> {
        try {
            const currentTime = Date.now();
            const internalRatingsLastCallTime = Storage.getInstance().getItem(
                Storage.Keys.INTERNAL_RATINGS_SERVICE_LAST_CALL_TIME,
            );
            let internalRatingsData = Storage.getInstance().getItem(
                Storage.Keys.INTERNAL_RATINGS_SERVICE_DATA,
            );
            const isServiceCallRequired: boolean =
                !internalRatingsLastCallTime ||
                internalRatingsLastCallTime + CachingPolicy.InternalRatingsService.cachingDuration <
                    currentTime;
            const isDataAvailable: boolean = internalRatingsData;

            if (isServiceCallRequired || !isDataAvailable) {
                await this.getInternalRatingsFromService();
                internalRatingsData = Storage.getInstance().getItem(
                    Storage.Keys.INTERNAL_RATINGS_SERVICE_DATA,
                );
            }

            if (internalRatingsData?.ratingValue)
                InternalRatingsServiceRepositoryImpl.INTERNAL_RATINGS_MAP = new Map(
                    internalRatingsData.ratingValue.map(({ ratingTypeId, ratingValueId, name }) => {
                        return [ratingTypeId + ratingValueId.replace('type', 'tivo'), name];
                    }),
                );
        } catch (error) {
            Log.e(this.TAG, 'fetchAndStoreBodyInfoData', error);
            throw error;
        }
    }

    getInternalRatingName(ratingKey: string): string {
        try {
            return InternalRatingsServiceRepositoryImpl.INTERNAL_RATINGS_MAP.get(ratingKey) || '';
        } catch (error) {
            Log.e(this.TAG, 'fetchInternalRatings', error);
            throw error;
        }
    }
}
